/**
 * AgedCareSuggestionEngineDeepDive.js
 * Deep Dive modal for the Aged Care Suggestion Engine: opportunity cost, yearly projections, asset breakdown.
 * Extracted from AgedCareSuggestionEnginePanel.js for maintainability.
 */
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { formatCurrency } from '../../utils/index.ts';
import { ProjectionsView, YearByYearHeader, yearByYearSubtitle } from './ProjectionsModal.tsx';
import { Button } from '../../components/ui/index.tsx';
import { planWords, type PlanRowForWords } from './planWords.ts';
import { comparedOverSentence } from '../advice/comparedOver.ts';
import { readPeriodMode, type ProjectionsModalPeriodMode } from './projectionsModalPreferences.ts';
import { formatRadStrategyDisplayName } from './futureLifeEventUtils.tsx';
import * as calculationApi from '../../services/calculationApi.ts';
import { applyAgedCareDownsizeToFactFind } from '../../../../shared/src/calculation/agedCareDownsizeFactFind.ts';
import type { FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import type { StrategyOptions as SharedStrategyOptions } from '../../../../shared/src/calculation/strategyOptionsTypes.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';

// Same asset-map shape as factFind.assets (see types/formDraft.ts) — each projection year row
// snapshots the fact-find's assets/liabilities as they stood that year.
type OpeningAssetsMap = NonNullable<DraftFactFind['assets']>;

// The suggestion engine's per-year projection row (calc-engine REST response — see calculationApi.ts's
// header on why these stay loosely typed rather than modelled 1:1) — only the fields this modal reads.
export interface DeepDiveProjectionBreakdownItem {
    label: string;
    value: string | number;
    change?: string;
}

interface DeepDiveFyProjectionRow {
    socialSecurity?: number;
    annualCareCost?: number;
    [key: string]: unknown;
}

interface DeepDiveFyFullYearView {
    daysPre?: number;
    daysPost?: number;
    fyLabel?: string;
    netWorthOpening?: number;
    blended?: {
        socialSecurity?: number;
        annualCareCost?: number;
        cashFlow?: number;
        tax?: number;
        livingExpenses?: number;
        [key: string]: unknown;
    };
    baselineProjectionRow?: DeepDiveFyProjectionRow;
    strategyProjectionRow?: DeepDiveFyProjectionRow;
    [key: string]: unknown;
}

export interface DeepDiveProjectionYear {
    netWorth?: number;
    cashFlow?: number;
    cashflow?: number;
    socialSecurity?: number;
    socialSecurityBreakdown?: DeepDiveProjectionBreakdownItem[];
    annualCareCost?: number;
    agedCareBreakdown?: DeepDiveProjectionBreakdownItem[];
    cashFlowBreakdown?: DeepDiveProjectionBreakdownItem[];
    /** Human-readable HTML breakdown of the year's calculations (see server `ProjectionYearResult.breakdown`). */
    breakdown?: string;
    cashflowRoutingLine?: DeepDiveProjectionBreakdownItem;
    tax?: number;
    fyLabel?: string;
    isPartialFy?: boolean;
    periodStart?: string;
    periodEnd?: string;
    home?: { value?: number; description?: string };
    assets?: OpeningAssetsMap;
    fyFullYearView?: DeepDiveFyFullYearView;
    [key: string]: unknown;
}

export interface DeepDiveScenarioSnapshot {
    pension?: number;
    totalAgedCareCosts?: number;
    investmentIncome?: number | string;
    livingExpenses?: number;
    agePensionBreakdown?: DeepDiveProjectionBreakdownItem[];
    [key: string]: unknown;
}

export interface DeepDiveScenarioStrategyOptions {
    downsizeHome?: boolean;
    downsizeDetails?: {
        newHomePurchasePrice?: number;
        radAllocation?: number;
        [key: string]: unknown;
    };
    sellingCosts?: number | string;
    rentAmount?: number;
    rentExpenses?: number;
    [key: string]: unknown;
}

export interface DeepDiveAssetAllocationItem {
    assetName: string;
    assetId?: string | number;
    status?: 'sold' | 'partiallySold' | 'retained' | string;
    amount?: number;
    liquidatedAmount?: number;
    [key: string]: unknown;
}

export interface DeepDiveScenario {
    name?: string;
    snapshot?: DeepDiveScenarioSnapshot;
    projections?: DeepDiveProjectionYear[];
    strategyOptions?: DeepDiveScenarioStrategyOptions;
    radAmount?: number;
    radFunding?: Record<string, number | string>;
    assetAllocation?: DeepDiveAssetAllocationItem[];
    originalFactFind?: { assets?: Record<string, Record<string, unknown>[]> };
    [key: string]: unknown;
}

interface DeepDiveAnalysis {
    strategies?: DeepDiveScenario[];
    [key: string]: unknown;
}

interface AgedCareSuggestionEngineDeepDiveProps {
    selectedScenario: DeepDiveScenario;
    factFind: DraftFactFind;
    agedCareCosts?: ({ scenarioLivingExpenses?: number } & Record<string, unknown>) | null;
    legacyYears: number;
    analysis?: DeepDiveAnalysis | null;
    activeTab: string;
    setActiveTab: (tab: string) => void;
    onSelectStrategy: () => void;
    onClose: () => void;
    applyRadFundingToFactFind: (factFind: DraftFactFind, scenario: unknown) => DraftFactFind;
    /*
     * The case-level parameters the ranking run was given. Carried in so the calendar-year re-run
     * below is the SAME evaluation with one thing changed — a re-run that quietly dropped the entry
     * date or the deposit arrangement would differ for reasons that have nothing to do with the
     * calendar, which is the whole failure this feature has to avoid.
     */
    careStartDate?: string;
    radDrawdown?: 'fees' | 'dap' | 'none';
    residentialFeeArrangements?: '2014' | '2025';
    /**
     * Above the year-by-year tabs: what the family receives from the plan's guaranteed income, and — for
     * income only — the confirmation (guaranteed income review E4/E5, 21 Oct 2026: "All tested" opens here).
     */
    beforeContent?: React.ReactNode;
    /** "Choose this plan" is held until the income-only confirmation is ticked. */
    selectHeld?: boolean;
    /** True when this is the plan already chosen: the button reads "Chosen ✓" and does nothing. */
    isChosen?: boolean;
}

export function AgedCareSuggestionEngineDeepDive({
    selectedScenario,
    factFind,
    agedCareCosts,
    legacyYears,
    analysis,
    activeTab,
    setActiveTab,
    onSelectStrategy,
    onClose,
    applyRadFundingToFactFind,
    careStartDate,
    radDrawdown,
    residentialFeeArrangements,
    beforeContent = null,
    selectHeld = false,
    isChosen = false,
}: AgedCareSuggestionEngineDeepDiveProps) {
    // Escape closes the window, as the X and a click outside it do (owner-approved mock-up, 25 Oct 2026).
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);
    // The plan's name as its card says it, not the engine's ("Pay 100% RAD, Keep Home (Bank -> ABP …)").
    const plainTitle = planWords(selectedScenario as PlanRowForWords, factFind, formatRadStrategyDisplayName(selectedScenario.name)).title;
    // Who is moving in, for "against paying the room daily": the person the aged care goal is for.
    const careFor = String((factFind?.goals as { aged_care_for?: string } | undefined)?.aged_care_for || 'client');
    const firstName = (who: 'client' | 'partner') => String(factFind?.[who]?.firstName || '').trim();
    const movingIn = careFor === 'both'
        ? [firstName('client'), firstName('partner')].filter(Boolean).join(' and ') || 'them'
        : firstName(careFor === 'partner' ? 'partner' : 'client') || 'them';
    const [year1OpeningBalance, setYear1OpeningBalance] = useState(0);
    // Post-downsize, post-RAD-funding factFind for Year 1 — computed once here (downsizing now runs
    // server-side) and reused below wherever the render body needs "opening state for Year 1", instead
    // of recomputing it synchronously inline (which can't await a network call).
    const [year1ModifiedFactFind, setYear1ModifiedFactFind] = useState<DraftFactFind | null>(null);

    useEffect(() => {
        let cancelled = false;
        // DraftFactFind is structurally compatible with FactFind/StrategyOptions
        // (see types/formDraft.ts); the shared calc-engine fn defensively coerces internally
        // regardless, and its own return type is `any` (agedCareDownsizeFactFind.ts).
        const baseFactFind: DraftFactFind = selectedScenario.strategyOptions?.downsizeHome
            ? applyAgedCareDownsizeToFactFind(factFind as unknown as FactFind, selectedScenario.strategyOptions as SharedStrategyOptions)
            : factFind;
        const modifiedFactFind = applyRadFundingToFactFind(baseFactFind, selectedScenario);
        setYear1ModifiedFactFind(modifiedFactFind);
        calculationApi.calculateFinancials(modifiedFactFind, {}, 0).then((financials) => {
            if (!cancelled) setYear1OpeningBalance(financials.netWorth || 0);
        });
        return () => { cancelled = true; };
    }, [selectedScenario, factFind, applyRadFundingToFactFind]);

    /*
     * THE YEAR TOGGLE, BY RE-RUNNING THIS ONE CANDIDATE (owner, 23 Sep 2026).
     *
     * The deep dive used to hide the financial/calendar toggle, and the reason given — that it had no
     * re-run pathway — was wrong: it is the same engine. What is true, and what decides the design, is
     * that the fact-find a candidate was PROJECTED from is derived on the server (the scenario
     * applied, the downsize applied, the means-test lever applied) and never sent here. Rebuilding it
     * on this side would produce a plausible set of rows for a subtly DIFFERENT plan — the worst kind
     * of wrong, because nothing on screen would say so.
     *
     * So the re-run goes back through the engine's own entry point with this one scenario in the
     * array. One derivation, no second implementation to drift. `agedCare.test.ts` pins that a
     * single-scenario run reproduces the row the whole field produced, which is what lets the reader
     * trust that this is the candidate that was ranked.
     */
    const [modalPeriodMode, setModalPeriodMode] = useState<ProjectionsModalPeriodMode>(readPeriodMode);
    const [calendarYearProjections, setCalendarYearProjections] = useState<DeepDiveProjectionYear[] | null>(null);
    useEffect(() => {
        if (modalPeriodMode !== 'calendar_year' || activeTab !== 'yearlyProjections') { setCalendarYearProjections(null); return undefined; }
        let cancelled = false;
        setCalendarYearProjections(null);
        // The scenario as the engine takes it — the same fields it was generated with, read back off
        // the row it produced.
        const scenario = {
            name: selectedScenario.name,
            radFunding: selectedScenario.radFunding ?? {},
            strategyOptions: selectedScenario.strategyOptions ?? {},
            ...(selectedScenario.groupKey ? { groupKey: selectedScenario.groupKey } : {}),
            ...(selectedScenario.fundingOrderLabel ? { fundingOrderLabel: selectedScenario.fundingOrderLabel } : {}),
            ...(selectedScenario.meansTestStrategy ? { meansTestStrategy: selectedScenario.meansTestStrategy } : {}),
        };
        calculationApi.evaluateAgedCareRadFundingScenarios(
            factFind,
            (agedCareCosts ?? {}) as never,
            [scenario] as never,
            legacyYears,
            careStartDate,
            radDrawdown,
            residentialFeeArrangements,
            'calendar_year',
        ).then((rows: unknown) => {
            if (cancelled) return;
            const first = Array.isArray(rows) ? (rows[0] as { projections?: DeepDiveProjectionYear[] } | undefined) : undefined;
            setCalendarYearProjections(first?.projections ?? []);
        });
        return () => { cancelled = true; };
    }, [modalPeriodMode, activeTab, selectedScenario, factFind, agedCareCosts, legacyYears, careStartDate, radDrawdown, residentialFeeArrangements]);

    /**
     * Rendered into `document.body`, and this is the whole reason the modal was still boxed in after
     * being widened.
     *
     * `position: fixed` is relative to the VIEWPORT only while no ancestor establishes a containing
     * block — and `transform`, `filter` and `backdrop-filter` all do. This modal renders inside the
     * shared `Card`, which carries `backdrop-blur-xl`, so `fixed inset-0` was resolving against the
     * card's box rather than the screen: the overlay could never be wider than the column it sat in,
     * whatever max-width it was given. Manual Control's projections modal is not inside a Card, which
     * is why the identical markup behaves differently there.
     *
     * A portal removes the dependency entirely rather than relying on no future ancestor introducing
     * one. The Save Warning modal in the sibling panel already does this.
     */
    return createPortal(
<div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-2 sm:p-4" onClick={onClose} role="presentation">
                    {/* Same shell as every module's year-by-year window, deliberately: the header and footer are
                        fixed and the BODY scrolls (`flex flex-col overflow-hidden` + an `overflow-y-auto` child),
                        so the plan's name and Choose button stay in view while someone reads down the figures. */}
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-screen-2xl max-h-[94vh] sm:max-h-[90vh] flex flex-col overflow-hidden border border-slate-200/80" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="deep-dive-title">
                        <YearByYearHeader
                            titleId="deep-dive-title"
                            title={plainTitle}
                            subtitle={`${yearByYearSubtitle(legacyYears)} in care${isChosen ? ' · the plan chosen' : ''}`}
                            onClose={onClose}
                            actions={isChosen ? (
                                <span className="inline-flex h-9 items-center rounded-lg border border-emerald-600 px-3.5 text-sm font-semibold text-emerald-700" data-testid="deep-dive-chosen">Chosen ✓</span>
                            ) : (
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="primary"
                                    onClick={() => { if (!selectHeld) onSelectStrategy(); }}
                                    disabled={selectHeld}
                                    title={selectHeld ? 'Tick the confirmation below first' : undefined}
                                    data-testid="deep-dive-choose"
                                >
                                    Choose this plan
                                </Button>
                            )}
                        />
                        {/* The scroller. `min-h-0` is what actually lets it shrink inside the flex
                            column — without it a flex child refuses to go below its content height and
                            the card grows past the viewport instead of scrolling. */}
                        <div className="p-6 flex-1 min-h-0 overflow-y-auto">
                            {beforeContent && <div className="mb-6 space-y-3">{beforeContent}</div>}

                            {/* Tab Navigation */}
                            <div className="border-b border-gray-200 mb-6">
                                <nav className="-mb-px flex space-x-8">
                                    {['yearlyProjections', 'opportunityCost'].map((tab) => (
                            <button
                                            key={tab}
                                            type="button"
                                            onClick={() => setActiveTab(tab)}
                                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                                activeTab === tab
                                                    ? 'border-blue-600 text-blue-700'
                                                    : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300'
                                            }`}
                                        >
                                            {tab === 'opportunityCost' && 'What this plan changes'}
                                            {tab === 'yearlyProjections' && 'Year by year'}
                            </button>
                        ))}
                    </nav>
                </div>

                                 {/* Tab Content */}
                            <div className="space-y-6 transition-all duration-300 ease-in-out">
                        {activeTab === 'opportunityCost' && (() => {
                                    const doNothingScenario = analysis?.strategies?.find(s => s.name === 'Do Nothing');
                                    if (!doNothingScenario) {
                                        return <p className="text-sm text-slate-600">There is no figure for paying the room daily to compare against.</p>;
                                    }
                                    const num = (v: unknown): number => {
                                        const n = typeof v === 'number' ? v : parseFloat(String(v));
                                        return Number.isFinite(n) ? n : 0;
                                    };
                                    // THE figure: what is left to the family after the horizon under this plan, against
                                    // paying the room daily with nothing sold. Everything else on this tab is a yearly
                                    // DRIVER of it.
                                    const planEstate = num(selectedScenario.snapshot?.legacyValue);
                                    const baseEstate = num(doNothingScenario.snapshot?.legacyValue);
                                    const legacyDelta = planEstate - baseEstate;
                                    // One sign convention throughout: positive = better off under this plan. Care costs are
                                    // therefore the SAVING (paying daily minus this plan).
                                    const pensionChange = num(selectedScenario.snapshot?.pension) - num(doNothingScenario.snapshot?.pension);
                                    const careSaving = num(doNothingScenario.snapshot?.totalAgedCareCosts) - num(selectedScenario.snapshot?.totalAgedCareCosts);
                                    const incomeChange = num(selectedScenario.snapshot?.investmentIncome) - num(doNothingScenario.snapshot?.investmentIncome);
                                    const same = (v: number) => Math.round(v) === 0;
                                    // Each panel says in one plain sentence why its figure moved (owner-approved mock-up).
                                    const drivers = [
                                        {
                                            key: 'pension',
                                            title: 'Age Pension',
                                            period: 'per year',
                                            why: same(pensionChange) ? 'The pension is the same either way.'
                                                : pensionChange > 0 ? 'The deposit is not counted in the means tests, so the pension rises.'
                                                : 'More is counted in the means tests under this plan, so the pension falls.',
                                            value: pensionChange,
                                        },
                                        {
                                            key: 'care',
                                            title: 'Aged care costs',
                                            period: 'per year',
                                            why: same(careSaving) ? 'Care costs are the same either way.'
                                                : careSaving > 0 ? 'Paying the room up front means less, or no, daily room fee.'
                                                : 'Care costs more each year under this plan.',
                                            value: careSaving,
                                        },
                                        {
                                            key: 'investment',
                                            title: 'Investment income',
                                            period: 'per year (Year 1)',
                                            why: same(incomeChange) ? 'Investment income is the same either way.'
                                                : incomeChange < 0 ? 'The money paid as the deposit stops earning. This is what the pension and care savings cost.'
                                                : 'More is left invested under this plan, so it earns more.',
                                            value: incomeChange,
                                        },
                                    ];
                                    // White panels; the figure keeps its colour, so a gain still reads green and a cost red.
                                    const valueTone = (v: number) => (same(v) ? 'text-slate-700' : v > 0 ? 'text-emerald-700' : 'text-red-700');
                                    const signed = (v: number) => `${v >= 0 ? '+' : ''}${formatCurrency(v)}`;
                                    return (
                                    <div className="space-y-6 animate-fadeIn">
                                        <div className="text-center mb-2">
                                            <h4 className="text-2xl font-bold text-brand-navy mb-2">What this plan changes, against paying the room daily</h4>
                                            <p className="text-slate-600">Each figure compares this plan with {movingIn} moving in and paying the whole room daily, nothing sold.</p>
                                        </div>

                                        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center sm:p-8" data-testid="deep-dive-estate">
                                            <h5 className="text-lg font-bold text-slate-900">Left to the family after about {legacyYears} {legacyYears === 1 ? 'year' : 'years'}</h5>
                                            <div className={`text-4xl font-bold mt-2 tabular-nums ${valueTone(legacyDelta)}`}>{signed(legacyDelta)}</div>
                                            <p className="text-sm text-slate-600 mt-3">
                                                {formatCurrency(planEstate)} against {formatCurrency(baseEstate)} if the room were paid daily. This one figure adds up every yearly effect below.
                                            </p>
                                        </div>

                                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                                            {drivers.map((driver) => (
                                                <div key={driver.key} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`deep-dive-driver-${driver.key}`}>
                                                    <div className="flex flex-col items-center text-center h-full">
                                                        <h5 className="text-lg font-bold text-slate-900 leading-tight mb-2">{driver.title}</h5>
                                                        <p className="text-sm text-slate-600 mb-4 leading-relaxed">{driver.why}</p>
                                                        <div className="mt-auto">
                                                            <div className={`text-3xl font-bold mb-1 tabular-nums ${valueTone(driver.value)}`}>{signed(driver.value)}</div>
                                                            <div className="text-sm text-slate-500 font-medium">{driver.period}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="rounded-xl border border-slate-200 bg-white p-6">
                                            <h5 className="text-lg font-semibold text-slate-900 mb-4 text-center">Where the differences come from</h5>
                                            <div className="divide-y divide-slate-200">
                                                {[
                                                    { name: 'Age Pension, a year', doNothing: num(doNothingScenario.snapshot?.pension), selected: num(selectedScenario.snapshot?.pension), betterWhenHigher: true },
                                                    { name: 'Aged care costs, a year', doNothing: num(doNothingScenario.snapshot?.totalAgedCareCosts), selected: num(selectedScenario.snapshot?.totalAgedCareCosts), betterWhenHigher: false },
                                                    { name: 'Investment income, first year', doNothing: num(doNothingScenario.snapshot?.investmentIncome), selected: num(selectedScenario.snapshot?.investmentIncome), betterWhenHigher: true },
                                                    { name: `Left to the family after about ${legacyYears} years`, doNothing: baseEstate, selected: planEstate, betterWhenHigher: true },
                                                ].map((row) => {
                                                    const advantage = row.betterWhenHigher ? row.selected - row.doNothing : row.doNothing - row.selected;
                                                    return (
                                                        <div key={row.name} className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-center py-3">
                                                            <div className="text-sm font-semibold text-slate-800">{row.name}</div>
                                                            <div className="text-center">
                                                                <div className="text-xs text-slate-500">Paying daily</div>
                                                                <div className="text-base font-bold text-slate-800 tabular-nums">{formatCurrency(row.doNothing)}</div>
                                                            </div>
                                                            <div className="text-center">
                                                                <div className="text-xs text-slate-500">This plan</div>
                                                                <div className="text-base font-bold text-brand-navy tabular-nums">{formatCurrency(row.selected)}</div>
                                                            </div>
                                                            <div className="text-center">
                                                                <div className="text-xs text-slate-500">{row.betterWhenHigher ? 'Difference' : 'Saving'}</div>
                                                                <div className={`text-base font-bold tabular-nums ${valueTone(advantage)}`}>{signed(advantage)}</div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <p className="text-xs text-slate-500 mt-4">
                                                Yearly figures are first-year rates. The {legacyYears}-year figure will not equal the yearly
                                                differences multiplied out: balances compound, the Age Pension and care fees are reassessed each
                                                year as assets change, and one-off effects (like capital gains tax on assets sold) land once.
                                            </p>
                                            <p className="text-[13px] text-slate-500 mt-2" data-testid="deep-dive-compared-over">{comparedOverSentence(legacyYears)}</p>
                                        </div>
                                    </div>
                                    );
                                })()}
                                {activeTab === 'yearlyProjections' && (
                                    <div className="animate-fadeIn -mx-6">
                                        {/* The SAME projections view the manual control's modal renders — charts accordion,
                                            "How to read this", the table with its tooltips, per-year month-by-month
                                            calculation breakdowns, and the future/today's-dollars toggle — so the two
                                            surfaces cannot drift apart. Differences are inputs, not code:
                                            - agedCareCosts: this STRATEGY's costs (RAD paid => lower DAP), not the panel's.
                                            - factFind/currentFinancials: the post-strategy year-1 opening position.
                                            - the financial/calendar toggle re-runs THIS candidate through the
                                              engine (see the effect above), because the fact-find it was
                                              projected from never leaves the server. */}
                                        <ProjectionsView
                                            projections={(modalPeriodMode === 'calendar_year' && calendarYearProjections ? calendarYearProjections : (selectedScenario.projections ?? [])) as never}
                                            onPeriodModeChange={setModalPeriodMode}
                                            isReloading={modalPeriodMode === 'calendar_year' && !calendarYearProjections}
                                            agedCareCosts={((selectedScenario as { strategySpecificAgedCareCosts?: Record<string, unknown> }).strategySpecificAgedCareCosts ?? agedCareCosts) as never}
                                            factFind={year1ModifiedFactFind ?? factFind}
                                            currentFinancials={{ netWorth: year1OpeningBalance }}
                                            comparedOverYears={legacyYears}
                                        />
                                    </div>
                                )}
            </div>
            </div>
                    </div>
                </div>,
        document.body,
);
}
